import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Collapse,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import type {
  ClearFolderCacheResponse,
  RebuildFolderCacheResponse,
} from '../../types/admin/settings'

interface FolderCacheResultDialogProps {
  open: boolean
  onClose: () => void
  result: ClearFolderCacheResponse | RebuildFolderCacheResponse | null
  mode: 'clear' | 'rebuild'
}

// Type guard to check if result is RebuildFolderCacheResponse
const isRebuildResult = (
  result: ClearFolderCacheResponse | RebuildFolderCacheResponse
): result is RebuildFolderCacheResponse => {
  return 'tablesEnqueued' in result
}

const FolderCacheResultDialog: React.FC<FolderCacheResultDialogProps> = ({
  open,
  onClose,
  result,
  mode,
}) => {
  const [showPaths, setShowPaths] = useState(false)

  if (!result) return null

  const isRebuild = mode === 'rebuild' && isRebuildResult(result)
  const paths = isRebuild
    ? (result as RebuildFolderCacheResponse).enqueuedPaths
    : (result as ClearFolderCacheResponse).affectedPaths

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === 'clear' ? 'Folder Cache Cleared' : 'Folder Cache Rebuild Started'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Typography variant="body1" paragraph>
            {mode === 'clear'
              ? `Cleared cache entries for folder "${result.folderPath}"`
              : `Cache rebuild initiated for folder "${result.folderPath}"`}
          </Typography>

          <Box sx={{ ml: 2 }}>
            {mode === 'clear' && !isRebuildResult(result) && (
              <>
                <Typography variant="body2">
                  Table Caches Cleared:{' '}
                  <strong>{(result as ClearFolderCacheResponse).tableCacheCount}</strong>
                </Typography>
                <Typography variant="body2">
                  Analysis Caches Cleared:{' '}
                  <strong>{(result as ClearFolderCacheResponse).analysisCacheCount}</strong>
                </Typography>
              </>
            )}

            {mode === 'rebuild' && isRebuild && (
              <>
                <Typography variant="body2">
                  Caches Cleared: <strong>{(result as RebuildFolderCacheResponse).clearedCount}</strong>
                </Typography>
                <Typography variant="body2">
                  Tables Enqueued for Rebuild:{' '}
                  <strong>{(result as RebuildFolderCacheResponse).tablesEnqueued}</strong>
                </Typography>
              </>
            )}
          </Box>

          {paths && paths.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => setShowPaths(!showPaths)}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {mode === 'clear' ? 'Affected Paths' : 'Enqueued Paths'} ({paths.length})
                </Typography>
                <IconButton
                  size="small"
                  aria-expanded={showPaths}
                  aria-label={showPaths ? 'Hide affected paths' : 'Show affected paths'}
                >
                  {showPaths ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={showPaths}>
                <List dense sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'grey.50', borderRadius: 1 }}>
                  {paths.map((path, index) => (
                    <ListItem key={index} sx={{ py: 0 }}>
                      <ListItemText
                        primary={path}
                        primaryTypographyProps={{ variant: 'body2', sx: { fontFamily: 'monospace' } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default FolderCacheResultDialog
